import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Bidirectional / account-reuse (GUNDI-5595): action configurations are stored on
// the INTEGRATION record, not on the Route, so every Route using this account
// inherits changes made here. Field mappings are NOT affected — those live on the
// Route and stay per-flow.
type SharedAccountNoticeProps = {
  // name is absent when the user can't resolve the Route (e.g. it lives in
  // a workspace they can't see) — those render as a count, never as ids/links.
  usedBy: { id: string; name?: string }[];
};

const SharedAccountNotice = ({ usedBy }: SharedAccountNoticeProps) => {
  const { t } = useTranslation();
  if (usedBy.length === 0) return null;
  const named = usedBy.filter((c) => c.name);
  const hiddenCount = usedBy.length - named.length;
  return (
    // Border only, no fill — a full yellow wash overstates an FYI.
    <div className="mb-5 rounded border border-unlisted-orange-200 px-4 py-3">
      <div className="text-sm text-ui-text-primary-text">
        <span className="font-semibold">{t('Shared account.')}</span>{' '}
        {t('Changes to these settings can affect other Routes.')}
      </div>
      {(named.length > 0 || hiddenCount > 0) && (
        <div className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1">
          {named.map((c, i) => (
            <span key={c.id} className="text-sm text-ui-text-primary-text">
              <Link
                to={`/routes/${c.id}`}
                // Same look as the source links in the Sources tables.
                className="text-brand-accent-green hover:underline"
              >
                {c.name}
              </Link>
              {(i < named.length - 1 || hiddenCount > 0) && ','}
            </span>
          ))}
          {hiddenCount > 0 && (
            <span className="text-sm text-ui-text-primary-text">
              {hiddenCount === 1
                ? t("{{count}} Route in a workspace you can't view", {
                    count: hiddenCount,
                  })
                : t("{{count}} Routes in workspaces you can't view", {
                    count: hiddenCount,
                  })}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default SharedAccountNotice;
